import { AnimatePresence, motion } from 'framer-motion';
import React, { useRef, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import html from 'react-syntax-highlighter/dist/esm/languages/hljs/htmlbars';
import css from 'react-syntax-highlighter/dist/esm/languages/hljs/css';
import docco from 'react-syntax-highlighter/dist/esm/styles/hljs/docco';
import { useClipboard } from 'use-clipboard-copy';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useAppSelector } from '../../redux/hooks';
import Icon from '../../styled/Icon/Icon';
import Switch from '../../styled/Switch';
import boxTemplates from '../../templates/box';
import {
  filterSrcMap,
  minifyCode,
  prettifyCSS,
  prettifyHTML,
} from '../../utils/formattingUtils';

import {
  CodeModalBody,
  CodeModalFooter,
  CodeModalHeader,
  CodeModalOuter,
  CodeModalWrapper,
  CodePrintPre,
  CodeRendererWrapper,
} from './CodeRenderer.styles';
import { useDisableBodyScroll } from '../../hooks/useDisableBodyScroll';
import { useRouteParams } from '../../hooks/useRouteParams';
import { trackMixEvent, TRACK_EVENTS } from '../../services/analytics';

SyntaxHighlighter.registerLanguage('html', html);
SyntaxHighlighter.registerLanguage('css', css);
interface CodeRendererProps {}

const codeModalVarients = {
  initial: {
    opacity: 0,
    rotateX: -20,
    y: -25,
    transition: {
      duration: 0.2,
      delay: 0.2,
    },
  },
  visible: {
    opacity: 1,
    rotateX: 0,
    y: 0,
    transition: {
      duration: 0.2,
    },
  },
  hidden: {
    opacity: 0,
    rotateX: 20,
    y: 25,
    transition: {
      duration: 0.2,
      delay: 0.2,
    },
  },
};

enum CODE_TAB {
  HTML,
  CSS,
  Combined,
}

const CodeRenderer: React.FC<CodeRendererProps> = () => {
  const codeModalRef = useRef(null);
  const codeEditorRef = useRef<HTMLPreElement>(null);
  const clipboard = useClipboard({ copiedTimeout: 1000 });
  const { type, template } = useRouteParams();
  const storeConfiguration = useAppSelector(
    ({ configuration }) => configuration
  );
  const boxData = useAppSelector(({ schema }) => schema);
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [currentTab, setCurrentTab] = useState(CODE_TAB.Combined);
  const [compressCode, setCompressCode] = useState(true);
  const [modalExpanded, setModalExpanded] = useState(false);

  const onClickOutside = () => {
    trackMixEvent(TRACK_EVENTS.CLOSE_CODE_MODAL);
    setModalExpanded(false);
    setShowCodeModal(false);
  };

  useClickOutside(codeModalRef, onClickOutside);
  useDisableBodyScroll(showCodeModal);
  const templatesArr = boxTemplates[type];

  const componentData = templatesArr.find(({ name }) => name === template);
  const BoxComponent = componentData?.component;

  const { global: globalConfiguration, template: templateConfiguration } =
    storeConfiguration;
  const colorsArr = globalConfiguration.enableGlobalColors
    ? globalConfiguration.colors
    : templateConfiguration[type]?.[template]?.colors;

  // Rendered Code
  if (!BoxComponent || boxData.type === '@init') return null;
  const rawHTML = ReactDOMServer.renderToStaticMarkup(
    <BoxComponent formData={boxData} config={globalConfiguration.config} />
  );
  const colorVariablesString =
    colorsArr &&
    colorsArr
      .map(({ colorId, colorValue }) => `--tk-${colorId}: ${colorValue};`)
      .join('');
  const rawStyles = componentData?.styles
    .toString()
    .replace('{', `{${colorVariablesString}`);
  const prettyHTML = prettifyHTML(rawHTML);
  const prettyStyles = prettifyCSS(filterSrcMap(rawStyles.toString()));
  const miniHTML = minifyCode(rawHTML);
  const miniStyles = minifyCode(filterSrcMap(rawStyles.toString()));
  const miniCombined = `<style>${miniStyles}</style>${miniHTML}`;
  const prettyCombined = `<style>
  ${prettyStyles}  
  </style>
  ${prettyHTML}`;

  const renderCode = {
    [CODE_TAB.HTML]: compressCode ? miniHTML : prettyHTML,
    [CODE_TAB.CSS]: compressCode ? miniStyles : prettyStyles,
    [CODE_TAB.Combined]: compressCode ? miniCombined : prettyCombined,
  };

  const handleCopyCode = () => {
    if (!codeEditorRef.current) return;
    trackMixEvent(TRACK_EVENTS.COPY_CODE);
    clipboard.copy(
      codeEditorRef.current.getElementsByTagName('code')[0].innerText
    );
  };

  const handleOpenCodeModal = () => {
    trackMixEvent(TRACK_EVENTS.OPEN_CODE_MODAL);
    setShowCodeModal(true);
  };

  const handleChangeTab = (tab: CODE_TAB) => {
    // eslint-disable-next-line no-underscore-dangle
    trackMixEvent(TRACK_EVENTS.CODE_TAB_ + tab);
    setCurrentTab(tab);
  };

  const handleMinifyCode = (e: React.ChangeEvent<HTMLInputElement>) => {
    trackMixEvent(TRACK_EVENTS.MINIFY_CODE_MODAL + e.target.checked);
    setCompressCode(e.target.checked);
  };

  const handleExpandCodeModal = () => {
    trackMixEvent(
      modalExpanded
        ? TRACK_EVENTS.MINIMISE_CODE_MODAL
        : TRACK_EVENTS.EXPAND_CODE_MODAL
    );
    setModalExpanded((st) => !st);
  };
  return (
    <CodeRendererWrapper>
      <button
        type="button"
        onClick={handleOpenCodeModal}
        className="codeToggle"
      >
        View Code <Icon type="code" />
      </button>
      <AnimatePresence>
        {showCodeModal && (
          <CodeModalOuter>
            <CodeModalWrapper
              ref={codeModalRef}
              as={motion.main}
              variants={codeModalVarients}
              initial="initial"
              animate="visible"
              exit="hidden"
              aria-expanded={modalExpanded}
            >
              <CodeModalHeader>
                <div className="tabs">
                  <button
                    role="tab"
                    aria-selected={currentTab === CODE_TAB.Combined}
                    aria-controls="code-panel"
                    type="button"
                    onClick={() => handleChangeTab(CODE_TAB.Combined)}
                  >
                    Combined
                  </button>
                  <button
                    role="tab"
                    aria-selected={currentTab === CODE_TAB.HTML}
                    aria-controls="code-panel"
                    type="button"
                    onClick={() => handleChangeTab(CODE_TAB.HTML)}
                  >
                    HTML
                  </button>
                  <button
                    role="tab"
                    aria-selected={currentTab === CODE_TAB.CSS}
                    aria-controls="code-panel"
                    type="button"
                    onClick={() => handleChangeTab(CODE_TAB.CSS)}
                  >
                    CSS
                  </button>
                </div>
                <div className="extraCta">
                  <span className="toggle">
                    Minify
                    <Switch
                      checked={compressCode}
                      varient="small"
                      className="switch"
                      onChange={handleMinifyCode}
                    />
                  </span>
                  <button
                    type="button"
                    className="expandBtn"
                    onClick={handleExpandCodeModal}
                  >
                    <Icon type="expand" height={20} width={20} />
                  </button>
                  <button
                    type="button"
                    className="expandBtn"
                    onClick={onClickOutside}
                  >
                    <Icon type="cross" height={20} width={20} />
                  </button>
                </div>
              </CodeModalHeader>
              <CodeModalBody
                role="tabpanel"
                tabIndex={0}
                id="code-panel"
                ref={codeEditorRef}
              >
                <CodePrintPre
                  contentEditable
                  wrapLongLines
                  as={SyntaxHighlighter}
                  language={currentTab === CODE_TAB.CSS ? 'css' : 'html'}
                  style={docco}
                  data-gramm="false"
                  spellCheck="false"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                >
                  {renderCode[currentTab]}
                </CodePrintPre>
              </CodeModalBody>
              <CodeModalFooter>
                <div className="footerCta">
                  <button
                    type="button"
                    onClick={handleCopyCode}
                    data-success={clipboard.copied}
                  >
                    <Icon
                      type={clipboard.copied ? 'copy-circle' : 'clipboard'}
                      height={16}
                      width={16}
                    />{' '}
                    {clipboard.copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </CodeModalFooter>
            </CodeModalWrapper>
          </CodeModalOuter>
        )}
      </AnimatePresence>
    </CodeRendererWrapper>
  );
};

export default CodeRenderer;
